// ─── Research Figure Engine ────────────────────────────────────────────────────
// PIPELINE B: Generates SVG charts for research chapters (Ch4+).
// Requires thesis_research_results data from the Research Lab.
//
// Detects: regression results, correlation matrices, descriptive statistics.
// Generates: coefficient plots, correlation heatmaps, descriptive bar charts.
// Injects: as data-thesis-figure chart-svg tokens (fully self-contained).
//
// Only operates on chapters with chapter_number >= 4.
// Disabled / skipped if no research results are present.

import { buildResearchFigureHTML, wrapFigureToken, injectFigureAfterParagraph } from './inlineFigureRenderer'
import {
  coefficientPlotSVG,
  correlationHeatmapSVG,
  histogramSVG,
} from './research/chartEngine'

// ── Detection cues ────────────────────────────────────────────────────────────

const REGRESSION_CUES = [
  /regression\s+(?:coefficient|result|model|analysis|output)/i,
  /OLS\s+(?:result|output|regression|analysis)/i,
  /coefficient\s+estimate/i,
  /standardis[e]?d?\s+(?:beta|coefficient)/i,
  /\bR[²2]\b|\bR-squared\b|\badj(?:usted)?\s+R/i,
  /significant\s+predictor/i,
  /predictor\s+variable/i,
]

const CORRELATION_CUES = [
  /correlation\s+matrix/i,
  /bivariate\s+correlation/i,
  /inter-?variable\s+correlation/i,
  /Pearson\s+correlation/i,
  /correlation\s+coefficient/i,
  /variable\s+relationship/i,
]

const DESCRIPTIVE_CUES = [
  /descriptive\s+statistic/i,
  /mean\s+(?:score|value|response)/i,
  /mean\s+and\s+standard\s+deviation/i,
  /summary\s+statistic/i,
  /central\s+tendency/i,
]

// ── Helpers ───────────────────────────────────────────────────────────────────

function stripHtml(html) {
  return (html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ')
}

function chapterType(title = '') {
  if (/result|finding|analys/i.test(title)) return 'results'
  if (/discussion|interpret/i.test(title)) return 'discussion'
  if (/method/i.test(title)) return 'methodology'
  return 'other'
}

function alreadyHasFigure(html, figureId) {
  return (html || '').includes(`data-figure-id="${figureId}"`)
}

function countExistingFigures(html, chNum) {
  const re = new RegExp(`data-figure-number="${chNum}\\.\\d+"`, 'g')
  return ((html || '').match(re) || []).length
}

function hasAnySvgFigure(html) {
  return /data-figure-type="chart-svg"/i.test(html || '')
}

// ── Descriptive bar chart (inline — no external dep) ─────────────────────────

function descriptiveBarSVG({ variables, means, stdDevs, title }) {
  const n = variables.length
  if (!n) return null

  const W = 560, H = 380
  const mL = 70, mR = 20, mT = 50, mB = 80
  const plotW = W - mL - mR
  const barW  = Math.min(55, Math.floor(plotW / n) - 10)
  const allVals = [...means, ...means.map((m, i) => m + (stdDevs[i] || 0))]
  const maxVal  = Math.max(...allVals) * 1.15 || 5

  const yScale = v => mT + (1 - v / maxVal) * (H - mT - mB)
  const xPos   = i => mL + (i + 0.5) * (plotW / n)

  const COLOURS = ['#2563eb', '#059669', '#7c3aed', '#d97706', '#dc2626', '#0d9488']
  let bars = '', axes = '', labels = ''

  means.forEach((m, i) => {
    const x    = xPos(i)
    const yTop = yScale(m)
    const yBot = yScale(0)
    const h    = Math.max(0, yBot - yTop)
    const col  = COLOURS[i % COLOURS.length]
    const sd   = stdDevs[i] || 0
    const yErr = yScale(m + sd)
    bars   += `<rect x="${x - barW / 2}" y="${yTop}" width="${barW}" height="${h}" fill="${col}" opacity="0.8"/>`
    bars   += `<line x1="${x}" y1="${yErr}" x2="${x}" y2="${yTop}" stroke="#334155" stroke-width="1.5"/>`
    bars   += `<line x1="${x - 5}" y1="${yErr}" x2="${x + 5}" y2="${yErr}" stroke="#334155" stroke-width="1.5"/>`
    labels += `<text x="${x}" y="${H - mB + 14}" text-anchor="middle" font-size="9" fill="#334155">${variables[i].replace(/_/g, ' ')}</text>`
    labels += `<text x="${x}" y="${yTop - 4}" text-anchor="middle" font-size="9" font-weight="bold" fill="#0f172a">${m.toFixed(2)}</text>`
  })

  for (let v = 0; v <= maxVal; v += maxVal / 4) {
    const y = yScale(v)
    axes += `<line x1="${mL}" y1="${y}" x2="${W - mR}" y2="${y}" stroke="#e2e8f0" stroke-width="1"/>`
    axes += `<text x="${mL - 6}" y="${y + 4}" text-anchor="end" font-size="9" fill="#64748b">${v.toFixed(1)}</text>`
  }

  axes += `<line x1="${mL}" y1="${mT}" x2="${mL}" y2="${H - mB}" stroke="#334155" stroke-width="1.5"/>`
  axes += `<line x1="${mL}" y1="${H - mB}" x2="${W - mR}" y2="${H - mB}" stroke="#334155" stroke-width="1.5"/>`

  const titleEl = title ? `<text x="${W / 2}" y="20" text-anchor="middle" font-size="12" font-weight="bold" fill="#0f172a">${title}</text>` : ''

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" font-family="'Times New Roman',Georgia,serif">
  <rect width="${W}" height="${H}" fill="white"/>
  ${titleEl}
  ${axes}${bars}${labels}
</svg>`
}

// ── Data extraction from research results ─────────────────────────────────────

// Normalize OLS/regression result into { coefficients, dvLabel, rSquared } for coefficientPlotSVG.
// coefficients: { varName: { beta, p_value, ci_lower, ci_upper, significant, marginal } }
function extractCoefficients(ols, variables = []) {
  if (!ols) return null
  const raw = ols.coefficients || ols.results?.coefficients
  if (!raw || typeof raw !== 'object') return null

  const labelOf = (key) => variables.find(v => v.name === key)?.label || key.replace(/_/g, ' ')
  const SKIP = new Set(['intercept', 'const', '_intercept', 'Intercept'])

  const coefficients = {}
  for (const [k, v] of Object.entries(raw)) {
    if (SKIP.has(k)) continue
    const entry = typeof v === 'object' && v !== null ? v : {}
    const beta     = Number(entry.beta ?? entry.coefficient ?? entry.coef ?? v ?? 0)
    const p_value  = Number(entry.p_value ?? entry.p ?? entry.pvalue ?? 0.05)
    const se       = Number(entry.se ?? entry.std_err ?? entry.std_error ?? Math.abs(beta * 0.3))
    const ci_lower = entry.ci_lower ?? entry.conf_low  ?? beta - 1.96 * se
    const ci_upper = entry.ci_upper ?? entry.conf_high ?? beta + 1.96 * se
    coefficients[labelOf(k)] = {
      beta, p_value,
      ci_lower: Number(ci_lower),
      ci_upper: Number(ci_upper),
      significant: p_value < 0.05,
      marginal:    p_value >= 0.05 && p_value < 0.10,
    }
    if (Object.keys(coefficients).length >= 8) break
  }

  if (Object.keys(coefficients).length < 2) return null

  return {
    coefficients,
    dvLabel:  ols.dependent_variable || ols.dv || 'Outcome',
    rSquared: ols.r_squared ?? ols.results?.r_squared ?? null,
  }
}

// Normalize correlation result into { matrix, pvalues, variables } for correlationHeatmapSVG.
// matrix/pvalues: nested { varName: { varName: value } } dict; variables: string[]
function extractCorrelation(corr, variables = []) {
  if (!corr) return null
  const rawMatrix  = corr.matrix || corr.correlation_matrix || corr.correlations
  const rawPValues = corr.pvalues || corr.p_values || corr.significance || {}
  const varNames   = corr.variables || (rawMatrix ? Object.keys(rawMatrix) : variables.slice(0, 6).map(v => v.name || v.label || v))

  if (!rawMatrix || !varNames?.length || varNames.length < 2) return null

  // Build nested pvalues dict if it's flat or missing
  const pvalues = {}
  varNames.forEach(vi => {
    pvalues[vi] = {}
    varNames.forEach(vj => {
      pvalues[vi][vj] = rawPValues?.[vi]?.[vj] ?? rawPValues?.[`${vi}_${vj}`] ?? null
    })
  })

  return { matrix: rawMatrix, pvalues, variables: varNames.slice(0, 8) }
}

// Normalize descriptives for the inline bar chart function.
function extractDescriptives(desc, variables = []) {
  if (!desc) return null
  const stats = desc.statistics || desc.descriptives || desc
  if (!stats || typeof stats !== 'object') return null

  const labelOf = (key) => variables.find(v => v.name === key)?.label || key.replace(/_/g, ' ')

  const entries = Object.entries(stats)
    .filter(([, v]) => v && typeof v === 'object' && ('mean' in v || 'Mean' in v))
    .slice(0, 7)

  if (entries.length < 2) return null

  return {
    variables: entries.map(([k]) => labelOf(k)),
    means:     entries.map(([, v]) => Number(v.mean ?? v.Mean ?? 0)),
    stdDevs:   entries.map(([, v]) => Number(v.std ?? v.SD ?? v.sd ?? 0)),
  }
}

// ── Main engine ───────────────────────────────────────────────────────────────

/**
 * Check whether a chapter has research results available to generate figures.
 */
export function hasResearchData(researchResults = {}) {
  return Object.keys(researchResults).length > 0
}

/**
 * Generate and inject research figures for a single chapter.
 * Only operates on Ch4+ (chapter_number >= 4).
 *
 * @param {{ id, title, content, chapter_number }} chapter
 * @param {{ researchResults, variables, thesis }} context
 * @returns {{ html: string, injected: Array, skipped: string[] }}
 */
export function generateResearchFigures(chapter, context = {}) {
  const { researchResults = {}, variables = [], thesis = {} } = context
  const chNum = chapter.chapter_number || 1

  if (chNum < 4) {
    return { html: chapter.content, injected: [], skipped: [`Ch${chNum}: research figures are for Ch4+ only`] }
  }

  if (!hasResearchData(researchResults)) {
    return { html: chapter.content, injected: [], skipped: ['No research results available — run the Research Lab first'] }
  }

  const plain   = stripHtml(chapter.content)
  const type    = chapterType(chapter.title)
  const injected = []
  const skipped  = []
  let html      = chapter.content
  let figNum    = countExistingFigures(html, chNum) + 1

  const ols  = researchResults.regression   || researchResults.ols || researchResults.multivariate_regression
  const corr = researchResults.correlation  || researchResults.correlation_matrix
  const desc = researchResults.descriptive  || researchResults.descriptives || researchResults.descriptive_statistics

  // ── Coefficient plot (Results / Discussion) ─────────────────────────────────
  if (REGRESSION_CUES.some(re => re.test(plain)) && (type === 'results' || type === 'discussion' || type === 'other')) {
    const figId = `res_${chNum}_coef`
    if (alreadyHasFigure(html, figId)) {
      skipped.push('coefficient_plot: already present')
    } else {
      const coefData = extractCoefficients(ols, variables)
      if (coefData) {
        const svg = coefficientPlotSVG({ coefficients: coefData.coefficients, dvLabel: coefData.dvLabel })
        if (svg) {
          const r2Str = coefData.rSquared != null ? ` (R² = ${Number(coefData.rSquared).toFixed(3)})` : ''
          const token = buildResearchFigureHTML({
            figureId: figId, svgCode: svg,
            title:   `Regression Coefficient Plot${r2Str}`,
            caption: 'Standardised regression coefficients with 95% confidence intervals. Asterisks denote statistical significance (p < .05).',
            chapterNumber: chNum, figureNumber: figNum,
          })
          html = injectFigureAfterParagraph(html, wrapFigureToken(token), REGRESSION_CUES)
          injected.push({ figureId: figId, chapterTitle: chapter.title, chapterNumber: chNum, figureNumber: figNum, title: 'Coefficient Plot', figureType: 'coefficient_plot' })
          figNum++
        } else skipped.push('coefficient_plot: svg generation returned null')
      } else skipped.push('coefficient_plot: insufficient regression coefficient data')
    }
  }

  // ── Correlation heatmap (Results / Methodology) ─────────────────────────────
  if (CORRELATION_CUES.some(re => re.test(plain)) && (type === 'results' || type === 'methodology')) {
    const figId = `res_${chNum}_heatmap`
    if (alreadyHasFigure(html, figId)) {
      skipped.push('correlation_heatmap: already present')
    } else {
      const corrData = extractCorrelation(corr, variables)
      if (corrData) {
        const svg = correlationHeatmapSVG({ matrix: corrData.matrix, pvalues: corrData.pvalues, variables: corrData.variables })
        if (svg) {
          const token = buildResearchFigureHTML({
            figureId: figId, svgCode: svg,
            title:   'Bivariate Correlation Heatmap',
            caption: 'Pearson correlation coefficients among the primary study variables. Colour intensity indicates correlation strength; red denotes negative correlations.',
            chapterNumber: chNum, figureNumber: figNum,
          })
          html = injectFigureAfterParagraph(html, wrapFigureToken(token), CORRELATION_CUES)
          injected.push({ figureId: figId, chapterTitle: chapter.title, chapterNumber: chNum, figureNumber: figNum, title: 'Correlation Heatmap', figureType: 'correlation_heatmap' })
          figNum++
        } else skipped.push('correlation_heatmap: svg generation returned null')
      } else skipped.push('correlation_heatmap: insufficient correlation matrix data')
    }
  }

  // ── Descriptive bar chart (Results only, if no coef plot already injected) ──
  if (
    DESCRIPTIVE_CUES.some(re => re.test(plain)) &&
    type === 'results' &&
    !injected.some(g => g.figureType === 'coefficient_plot')
  ) {
    const figId = `res_${chNum}_desc`
    if (alreadyHasFigure(html, figId)) {
      skipped.push('descriptive_chart: already present')
    } else {
      const descData = extractDescriptives(desc, variables)
      if (descData) {
        const svg = descriptiveBarSVG({ ...descData, title: 'Descriptive Statistics by Variable' })
        if (svg) {
          const token = buildResearchFigureHTML({
            figureId: figId, svgCode: svg,
            title:   'Descriptive Statistics — Mean Scores and Standard Deviations',
            caption: 'Mean scores and standard deviations for all primary study variables. Error bars represent ±1 SD.',
            chapterNumber: chNum, figureNumber: figNum,
          })
          html = injectFigureAfterParagraph(html, wrapFigureToken(token), DESCRIPTIVE_CUES)
          injected.push({ figureId: figId, chapterTitle: chapter.title, chapterNumber: chNum, figureNumber: figNum, title: 'Descriptive Chart', figureType: 'descriptive_chart' })
          figNum++
        } else skipped.push('descriptive_chart: svg generation returned null')
      } else skipped.push('descriptive_chart: insufficient descriptive statistics data')
    }
  }

  return { html, injected, skipped }
}

/**
 * Quick scan — detect which research chapters are missing figures.
 */
export function scanResearchFigureGaps(chapters = [], researchResults = {}) {
  return chapters
    .filter(ch => (ch.chapter_number || 0) >= 4)
    .map(ch => ({
      chapterId:    ch.id,
      chapterTitle: ch.title,
      hasAnyFigure: hasAnySvgFigure(ch.content),
      hasData:      hasResearchData(researchResults),
    }))
    .filter(r => !r.hasAnyFigure && r.hasData)
}
